import type {
  CandidateRecord,
  PredictorDiagnostics,
  RadarFilters,
  DataFreshness,
  RadarDiagnostics,
} from '../types/market.js';
import { isPredictorCandidate, predictorBlockers } from './candidateQualification.js';
import { mergeCandidateWithLiveObservation } from './candidateLiveMerge.js';
import { computeStablePredictorOrder, mergeIssuedPredictions } from './predictorDisplay.js';


interface RadarSourceState {
  observations: CandidateRecord[];
  marketCount: number;
  updatedAt: number;
  freshness: DataFreshness;
  warmupProgress: number;
  diagnostics: RadarDiagnostics;
}

export interface RadarSnapshotView {
  predictor: CandidateRecord[];
  monitoring: CandidateRecord[];
  predictorEmptyMessage: string;
  diagnostics: PredictorDiagnostics;
  marketCount: number;
  updatedAt: number;
  freshness: DataFreshness;
  warmupProgress: number;
  connectionLabel: string;
  validationLabel: string;
  error?: string;
}
export interface SnapshotBuildState {
  issuedPredictions: Map<string, CandidateRecord>;
  stablePredictorOrder: string[];
  lastRankReflowAt: number;
}

function surfacedError(workerError?: string, timeSyncWarning?: string, archiveWarning?: string): string {
  return [workerError, timeSyncWarning, archiveWarning].filter(Boolean).join(' ');
}

function emptyMessage(source: RadarSourceState, rows: CandidateRecord[], diagnostics: PredictorDiagnostics, minimumDataQuality: number): string {
  if (source.marketCount === 0) return 'No live markets have been received. Check the connection status; Pilum does not substitute simulated opportunities.';
  if (source.warmupProgress < 100 && source.diagnostics.baselineReadyMarkets < 8) return `The radar is still building live movement baselines (${source.warmupProgress}% ready). Eagle Watch can appear before the Predictor because deep candidates require enough recent behavior history.`;
  if (diagnostics.deepFailed > 0 && diagnostics.deepComplete + diagnostics.deepPartial === 0) return `Directional observations were found, but deep candle history failed for ${diagnostics.deepFailed} candidate${diagnostics.deepFailed === 1 ? '' : 's'}. The engine keeps them in monitoring instead of silently promoting incomplete analysis.`;
  if (diagnostics.timingBlocked > 0 && diagnostics.timingBlocked >= rows.length) return `${diagnostics.timingBlocked} deeply checked observation${diagnostics.timingBlocked === 1 ? ' is' : 's are'} near the selected candle transition. Fresh same-horizon promotion is temporarily blocked until the next candle stabilizes.`;
  if (source.diagnostics.directionalObservations === 0 && source.diagnostics.neutralObservations > 0) return 'Live movement is being monitored, but no observation currently has enough contextual direction for a long or short candidate. Deep history may still upgrade an adaptive breakthrough or rejection.';
  const blockerCounts = new Map<string, number>();
  for (const row of rows) for (const blocker of predictorBlockers(row, minimumDataQuality)) blockerCounts.set(blocker, (blockerCounts.get(blocker) ?? 0) + 1);
  const leading = [...blockerCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2);
  if (leading.length > 0) return `No current observation passes every predictor gate. Main blockers: ${leading.map(([label, count]) => `${label} (${count})`).join('; ')}. Pilum abstains instead of forcing a prediction.`;
  return 'No candidate currently passes the selected evidence, timing, liquidity, and data-quality filters.';
}

function withSupport(candidate: CandidateRecord, supportingRecords: Map<string, { analysis: import('./supporting/types.js').SupportingAnalysis; lastEvaluatedAt: number }>): CandidateRecord {
  const support = supportingRecords.get(candidate.id);
  const score = support?.analysis.score ?? candidate.supportingScore ?? 0;
  return { ...candidate, coreScore: candidate.candidateScore, supportingScore: score, priorityScore: candidate.candidateScore + score, ...(support ? { supportingAnalysis: support.analysis } : {}) };
}

export function buildRadarSnapshot(args: {
  source: RadarSourceState;
  filters: RadarFilters;
  deepRecords: Map<string, CandidateRecord>;
  supportingRecords: Map<string, { analysis: import('./supporting/types.js').SupportingAnalysis; lastEvaluatedAt: number }>;
  state: SnapshotBuildState;
  serverOffsetMs: number;
  connectionLabel: string;
  validationLabel: string;
  workerError?: string;
  timeSyncWarning?: string;
  archiveWarning?: string;
}): { snapshot: RadarSnapshotView; state: SnapshotBuildState } {
  const { source, filters, deepRecords, supportingRecords } = args;
  const now = Date.now() + args.serverOffsetMs;
  const liveRows = source.observations.map((candidate) => withSupport(mergeCandidateWithLiveObservation(candidate, deepRecords.get(candidate.id)), supportingRecords));
  for (const candidate of liveRows) {
    const existing = args.state.issuedPredictions.get(candidate.id);
    if (isPredictorCandidate(candidate, filters.minDataQuality)) {
      args.state.issuedPredictions.set(candidate.id, { ...existing, ...candidate, predictionIssuedAt: existing?.predictionIssuedAt ?? now, predictionHeld: false, predictionLifecycle: candidate.state === 'reversed' ? 'reversed' : candidate.state === 'weakening' ? 'weakening' : 'active' });
    } else if (existing) {
      args.state.issuedPredictions.set(candidate.id, { ...existing, ...candidate, predictionIssuedAt: existing.predictionIssuedAt ?? now, predictionHeld: true, predictionLifecycle: candidate.state === 'reversed' ? 'reversed' : candidate.state === 'weakening' ? 'weakening' : 'active' });
    }
  }
  const rows = mergeIssuedPredictions(liveRows, args.state.issuedPredictions, now);
  const activePredictors = rows.filter((candidate) => isPredictorCandidate(candidate, filters.minDataQuality));
  const stickyIssued = rows.filter((candidate) => args.state.issuedPredictions.has(candidate.id) && !activePredictors.some((active) => active.id === candidate.id));
  const predictorCandidates = [...activePredictors, ...stickyIssued].filter((candidate, index, all) => all.findIndex((item) => item.id === candidate.id) === index).slice(0, 30);
  const stable = computeStablePredictorOrder(predictorCandidates, args.state.stablePredictorOrder, args.state.lastRankReflowAt, now);
  const state: SnapshotBuildState = { ...args.state, stablePredictorOrder: stable.order, lastRankReflowAt: stable.reflowAt };
  const predictor = stable.rows;
  const predictorIds = new Set(predictor.map((candidate) => candidate.id));
  const monitoring = rows.filter((candidate) => !predictorIds.has(candidate.id) && (candidate.candidateScore >= 34 || !candidate.baselineReady || ['weakening', 'invalidated', 'reversed'].includes(candidate.state))).sort((left, right) => {
    if (left.baselineReady !== right.baselineReady) return left.baselineReady ? -1 : 1;
    if (left.validationStatus !== right.validationStatus) { const order = { complete: 5, partial: 4, queued: 3, failed: 2, 'not-requested': 1 } as const; return order[right.validationStatus] - order[left.validationStatus]; }
    return right.candidateScore - left.candidateScore || right.movementStrength - left.movementStrength || left.id.localeCompare(right.id);
  }).slice(0, 50);
  const diagnostics: PredictorDiagnostics = {
    ...source.diagnostics,
    deepQueued: rows.filter((candidate) => candidate.validationStatus === 'queued').length,
    deepComplete: rows.filter((candidate) => candidate.validationStatus === 'complete').length,
    deepPartial: rows.filter((candidate) => candidate.validationStatus === 'partial').length,
    deepFailed: rows.filter((candidate) => candidate.validationStatus === 'failed').length,
    timingBlocked: rows.filter((candidate) => candidate.deepValidated && !candidate.freshIssuanceAllowed).length,
    qualityBlocked: rows.filter((candidate) => candidate.deepValidated && predictorBlockers(candidate, filters.minDataQuality).some((item) => item.includes('quality') || item.includes('evidence'))).length,
    promoted: activePredictors.length,
  };
  return {
    state,
    snapshot: { predictor, monitoring, diagnostics, predictorEmptyMessage: emptyMessage(source, rows, diagnostics, filters.minDataQuality), marketCount: source.marketCount, updatedAt: source.updatedAt, freshness: source.freshness, warmupProgress: source.warmupProgress, connectionLabel: args.connectionLabel, validationLabel: args.validationLabel, ...(surfacedError(args.workerError, args.timeSyncWarning, args.archiveWarning) ? { error: surfacedError(args.workerError, args.timeSyncWarning, args.archiveWarning) } : {}) },
  };
}
